import { useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input, Select } from "../ui/input";
import { formatDateTime } from "../../lib/format";
import {
  CRM_STAGE_LABELS,
  centsToInput,
  dateTimeLocalToIso,
  formatCents,
  formatRelativeDay,
  parseAmountToCents,
  toDateTimeLocalValue,
} from "../../lib/crm";
import { useToastStore } from "../../stores/toast";
import type { CrmOpportunity, CrmOpportunityStage } from "@shared/types";

interface Draft {
  title: string;
  stage: CrmOpportunityStage;
  setup: string;
  mrr: string;
  probability: string;
  expectedCloseAt: string;
  ownerUserId: string;
  lostReason: string;
  notes: string;
}

const EMPTY: Draft = {
  title: "",
  stage: "qualification",
  setup: "",
  mrr: "",
  probability: "50",
  expectedCloseAt: "",
  ownerUserId: "",
  lostReason: "",
  notes: "",
};

const CLOSED_STAGES: CrmOpportunityStage[] = ["gagnee", "perdue"];

interface OpportunityListProps {
  accountId: string;
  items: CrmOpportunity[];
  currency: string;
  users: Array<{ id: string; name: string }>;
  onChanged: () => void;
}

export function OpportunityList({
  accountId,
  items,
  currency,
  users,
  onChanged,
}: OpportunityListProps) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const base = `/api/crm/accounts/${accountId}/opportunities`;

  const reset = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim() || saving) return;
    const setupCents = draft.setup.trim() ? parseAmountToCents(draft.setup) : 0;
    const mrrCents = draft.mrr.trim() ? parseAmountToCents(draft.mrr) : 0;
    if (setupCents === null || mrrCents === null || setupCents < 0 || mrrCents < 0) {
      push("danger", "Montant invalide", "Saisis un montant positif, en dollars (ex : 149,00).");
      return;
    }
    setSaving(true);
    try {
      await api(editingId ? `${base}/${editingId}` : base, {
        method: editingId ? "PATCH" : "POST",
        body: JSON.stringify({
          title: draft.title.trim(),
          stage: draft.stage,
          setupCents,
          mrrCents,
          probability: Math.min(100, Math.max(0, Math.round(Number(draft.probability) || 0))),
          // Une date de clôture saisie sans heure est ancrée à midi : minuit local
          // bascule la veille dans les fuseaux à l'ouest de UTC.
          expectedCloseAt: dateTimeLocalToIso(
            draft.expectedCloseAt ? `${draft.expectedCloseAt}T12:00` : "",
          ),
          ownerUserId: draft.ownerUserId || null,
          lostReason: draft.lostReason.trim() || null,
          notes: draft.notes.trim() || null,
        }),
      });
      push("success", editingId ? "Opportunité mise à jour" : "Opportunité ajoutée");
      reset();
      onChanged();
    } catch {
      push("danger", "Échec de l'enregistrement de l'opportunité");
    } finally {
      setSaving(false);
    }
  };

  const edit = (o: CrmOpportunity) => {
    setEditingId(o.id);
    setDraft({
      title: o.title,
      stage: o.stage,
      setup: centsToInput(o.setupCents),
      mrr: centsToInput(o.mrrCents),
      probability: String(o.probability),
      expectedCloseAt: toDateTimeLocalValue(o.expectedCloseAt).slice(0, 10),
      ownerUserId: o.ownerUserId ?? "",
      lostReason: o.lostReason ?? "",
      notes: o.notes ?? "",
    });
  };

  const changeStage = async (id: string, stage: CrmOpportunityStage) => {
    try {
      await api(`${base}/${id}`, { method: "PATCH", body: JSON.stringify({ stage }) });
      onChanged();
    } catch {
      push("danger", "Échec du changement d'étape");
    }
  };

  const remove = async (id: string) => {
    try {
      await api(`${base}/${id}`, { method: "DELETE" });
      if (editingId === id) reset();
      push("success", "Opportunité supprimée");
      onChanged();
    } catch {
      push("danger", "Échec de la suppression");
    }
  };

  // Valeur pondérée du pipeline ouvert : (installation + 12 mois d'abonnement) x probabilité.
  const openWeightedCents = items
    .filter((o) => !CLOSED_STAGES.includes(o.stage))
    .reduce(
      (sum, o) => sum + Math.round(((o.setupCents + o.mrrCents * 12) * o.probability) / 100),
      0,
    );

  return (
    <section className="crm-section">
      <div className="crm-section__head">
        <h2 className="crm-section__title">Opportunités</h2>
        <div className="crm-section__actions">
          <span className="crm-chip crm-chip--muted">
            Pipeline pondéré {formatCents(openWeightedCents, currency)}
          </span>
        </div>
      </div>

      <form className="crm-form crm-form--inline" onSubmit={submit}>
        <Input
          placeholder="Opportunité (ex : agent vocal + réservations)"
          aria-label="Titre de l'opportunité"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <Select
          aria-label="Étape"
          value={draft.stage}
          onChange={(e) => setDraft({ ...draft, stage: e.target.value as CrmOpportunityStage })}
        >
          {Object.entries(CRM_STAGE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Input
          placeholder="Installation $"
          aria-label="Frais d'installation en dollars"
          value={draft.setup}
          onChange={(e) => setDraft({ ...draft, setup: e.target.value })}
        />
        <Input
          placeholder="MRR $"
          aria-label="Abonnement mensuel en dollars"
          value={draft.mrr}
          onChange={(e) => setDraft({ ...draft, mrr: e.target.value })}
        />
        <Input
          type="number"
          min={0}
          max={100}
          step={5}
          aria-label="Probabilité en pourcentage"
          title="Probabilité (%)"
          value={draft.probability}
          onChange={(e) => setDraft({ ...draft, probability: e.target.value })}
        />
        <Input
          type="date"
          aria-label="Clôture prévue"
          title="Clôture prévue"
          value={draft.expectedCloseAt}
          onChange={(e) => setDraft({ ...draft, expectedCloseAt: e.target.value })}
        />
        <Select
          aria-label="Responsable de l'opportunité"
          value={draft.ownerUserId}
          onChange={(e) => setDraft({ ...draft, ownerUserId: e.target.value })}
        >
          <option value="">Non assigné</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
        {editingId ? (
          <>
            <Input
              placeholder="Motif de perte"
              aria-label="Motif de perte"
              value={draft.lostReason}
              onChange={(e) => setDraft({ ...draft, lostReason: e.target.value })}
            />
            <Input
              placeholder="Notes"
              aria-label="Notes de l'opportunité"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </>
        ) : null}
        <div className="crm-form__actions">
          {editingId ? (
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              Annuler
            </Button>
          ) : null}
          <Button type="submit" size="sm" disabled={saving || !draft.title.trim()}>
            {editingId ? "Enregistrer" : "Ajouter"}
          </Button>
        </div>
      </form>

      <table className="table crm-table">
        <thead>
          <tr>
            <th>Opportunité</th>
            <th>Installation</th>
            <th>MRR</th>
            <th>Probabilité</th>
            <th>Clôture</th>
            <th>Étape</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={7} className="table__empty">
                Aucune opportunité — chiffre la proposition ci-dessus.
              </td>
            </tr>
          ) : (
            items.map((o) => {
              const owner = users.find((u) => u.id === o.ownerUserId);
              return (
                <tr key={o.id}>
                  <td data-label="Opportunité" title={o.notes ?? o.lostReason ?? undefined}>
                    {o.title}{" "}
                    {owner ? <span className="crm-chip crm-chip--muted">{owner.name}</span> : null}
                  </td>
                  <td data-label="Installation">
                    <span className="crm-money">{formatCents(o.setupCents, currency)}</span>
                  </td>
                  <td data-label="MRR">
                    <span className="crm-money">{formatCents(o.mrrCents, currency)}</span>
                  </td>
                  <td data-label="Probabilité">
                    {o.probability} %
                    <div className="crm-progress">
                      <div className="crm-progress__bar" style={{ width: `${o.probability}%` }} />
                    </div>
                  </td>
                  <td data-label="Clôture" title={formatDateTime(o.closedAt ?? o.expectedCloseAt)}>
                    {formatRelativeDay(o.closedAt ?? o.expectedCloseAt)}
                  </td>
                  <td data-label="Étape">
                    <Select
                      aria-label={`Étape de l'opportunité ${o.title}`}
                      value={o.stage}
                      onChange={(e) =>
                        void changeStage(o.id, e.target.value as CrmOpportunityStage)
                      }
                    >
                      {Object.entries(CRM_STAGE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="table__actions">
                    <Button variant="ghost" size="sm" onClick={() => edit(o)}>
                      Modifier
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => void remove(o.id)}>
                      Supprimer
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </section>
  );
}
